import { constants, VideoSVG } from '@newsela/angelou';
import deline from 'deline';
import gql from 'graphql-tag';
import { escapeRegExp } from 'lodash-es';
import smartquotes from 'smartquotes';

import { metadataSelectors } from '@client/utils/add-metadata-to-schema';
import { SINGLE_FIELD } from '@client/utils/constants';
import { maturityRangeField } from '@client/utils/maturity-range';

import Content from './content';
import ContentProvider from './content-provider';
import Stream from './stream';
import Tag from './tag';
import TaxonomyTag from './taxonomyTag';

const isWithinApp = () => window.location.href.match(/generation-genius/i) !== null;

const generationGeniusAppBaseUrl = process.env.CLIENT_BASE_PATH !== undefined
  ? `${process.env.CLIENT_BASE_PATH}${process.env.CLIENT_BASE_PATH.endsWith('/') ? '' : '/'}generation-genius`
  : '/generation-genius`';

const subjectProductStreamsSkipWithinDrawer = {
  ...Stream.inputs.subjectProductStreams,
  skip: () => !isWithinApp()
};

const customStreamsWithLabel = {
  ...Stream.inputs.customStreamsWithLite,
  label: (data) => !isWithinApp() ? 'Streams' : 'Additional streams'
};

const editorLinkShowInDrawer = {
  input: 'static',
  isLink: true,
  name: 'id',
  label: 'Manage Subject Product Streams in the Generation Genius Video App',
  skip: () => isWithinApp(),
  transform: (id) => id ? `${generationGeniusAppBaseUrl}/${id}` : null
};

const attachmentsSkipInApp = {
  ...Content.inputs.attached,
  skip: () => isWithinApp(),
};

const searchMappingWithinDrawer = {
  contentType: 'content_type',
  updatedAt: 'updated_at',
  updatedBy: 'updated_by',
};

const baseSearchConfig = {
  index: 'generation_genius_video',
  fields: ['title^10', 'slug^7', 'text^5', 'DIY^3'],
  sort: [
    {
      updated_at: {
        order: 'desc',
      },
    },
  ],
  // If the user included a query we disable the sort so we don't affect the ranking.
  disableSortOnQuery: true,
  _source: [
    'id',
    'content_type',
    'name',
    'slug',
    'status',
    'streams',
    'uid',
    'updated_at',
    'updated_by',
  ],
};

const openSearch = isWithinApp()
  ? baseSearchConfig
  : {
      ...baseSearchConfig,
      mapping: searchMappingWithinDrawer,
    };

function filter (query) {
  const quoted = smartquotes(query);

  return {
    or: [
      { title: { alloftext: quoted } },
      { text: { alloftext: quoted } },
      { DIY: { alloftext: quoted } },
      { slug: { regexp: `/${escapeRegExp(query)}/i` } },
    ],
  };
}

function quickFilter (query) {
  const quoted = smartquotes(query);

  return {
    title: { alloftext: quoted },
    rootFn: 'title|alloftext',
  };
}

const formConfig = [
  {
    input: 'prosemirror',
    name: 'title',
    value: 'rawTitle',
    formats: [],
  },
  {
    input: 'text',
    name: 'slug',
    value: 'slug',
  },
  {
    input: 'prosemirror',
    name: 'text',
    value: 'rawText',
    isMultiline: true,
    formats: [
      'bold',
      'italic',
      'headings',
      'quote',
      'language',
      'list',
      'orderedList',
      'link',
      'pre'
    ],
  },
  {
    input: 'generationGeniusWistiaPlayer',
    name: 'video',
    value: 'wistiaId',
  },
  maturityRangeField,
  {
    ...ContentProvider.inputs.contentProvider,
    required: true,
  },
  attachmentsSkipInApp,
  Tag.inputs.tags,
  ...metadataSelectors(),
  TaxonomyTag.inputs.taxonomyTags,
  {
    input: 'new-standard-selector',
    type: 'customFormField',
  },
  subjectProductStreamsSkipWithinDrawer,
  editorLinkShowInDrawer,
  customStreamsWithLabel,
  {
    input: 'generationGeniusPdfLinks',
    name: 'pdfs',
    value: 'pdfs',
  },
  {
    input: 'prosemirror',
    name: 'DIY',
    label: 'DIY Text',
    value: 'rawDIY',
    isMultiline: true,
    formats: [
      'bold',
      'italic',
      'headings',
      'quote',
      'language',
      'list',
      'orderedList',
      'link',
      'pre'
    ],
  }
];

const GenerationGeniusVideo = {
  previewFragment: gql`
    fragment previewGenerationGeniusVideo on GenerationGeniusVideo {
      title(format: TEXT_PLAIN)
    }
  `,
  fullFragment: gql`
    fragment fullGenerationGeniusVideo on GenerationGeniusVideo {
      discussionQuestions
      title: title(format: TEXT_RAW)
      text: text(format: TEXT_RAW)
      DIY: DIY(format: TEXT_RAW)
      slug
      pdfs
      vocabulary
      wistiaId
      maturityRange
    }
  `,
  filter,
  quickFilter,
  statusSelector: true,
  defaults: (id, data) => {
    const contentDefaults = Content.defaults(id, data);

    return {
      client: {
        ...contentDefaults.client,
        __typename: 'GenerationGeniusVideo',
        contentType: 'GENERATION_GENIUS_VIDEO',
        name: 'Generation Genius Video',
        title: null,
        text: null,
        DIY: null,
      },
      server: {
        ...contentDefaults.server,
        contentType: 'GENERATION_GENIUS_VIDEO',
      },
    };
  },
  isContent: true,
  icon: VideoSVG,
  typename: 'Generation Genius Video',
  openInApp: true,
  disableCreate: true,
  app: {
    icon: VideoSVG,
    color: constants.colors.ui.neutral[700],
    accentColor: constants.colors.ui.neutral[500],
    title: 'Generation Genius Videos',
    initialDrawer: {
      drawerType: SINGLE_FIELD,
      customDrawerTitle: 'Attachments',
      formKey: 'attachments',
    },
    href: '/generation-genius',
    permissionForAdmin: true,
    permissionForStaff: true,
    permissionForContributor: true,
    columns: [
      {
        value: 'id',
        showColumn: false,
      },
      {
        value: 'uid',
        showColumn: false,
      },
      {
        label: 'Title',
        value: 'name',
        isClickable: true,
      },
      {
        label: 'Slug',
        value: 'slug',
        isClickable: true,
      },
      {
        value: 'status',
        enum: true,
      },
      {
        label: 'Updated On',
        value: 'updated_at',
        date: true,
      },
      {
        label: 'Updated By',
        value: 'updated_by',
        list: 'name',
      },
    ],
    order: { desc: 'updatedAt' },
    rawFilter: (
      id
    ) => deline`var(func: eq(updatedByIds, ${id})) @filter(type(Event)) {
      ~events @filter(type(Content) and type(GenerationGeniusVideo) and not eq(visibility, -1)) {
        v as uid
      }
    }`,
    openSearch,
  },
  forms: {
    shared: [
      ...formConfig,
      Content.inputs.notes,
      Content.inputs.parentsList,
      Content.inputs.id,
    ],
    app: [
      ...formConfig,
      {
        input: 'textarea',
        name: 'discussionQuestions',
        value: 'discussionQuestions',
      },
      {
        input: 'textarea',
        name: 'vocabulary',
        value: 'vocabulary',
      },
      Content.inputs.notes,
      Content.inputs.parentsList,
      Content.inputs.id,
    ],
    attachments: [
      {
        ...Content.inputs.attached,
        editorTypes: [
          ...Content.inputs.attached.editorTypes,
          {
            type: 'ExternalLink',
            title: 'Formative',
          },
        ],
      },
    ],
  },
};

export default GenerationGeniusVideo;
